/** Envelope de getProvidersByService (Sourcing). */

export interface ProveedorTarifasItem {
  id?: string | null;
  nombre?: string | null;
  servicioId?: string | null;
  tipoProveedor?: string | null;
  correoReserva?: string | null;
  tarifa?: { id?: string | null } | null;
  oldServiceId?: string;
  [key: string]: unknown;
}

export interface GetProvidersByServiceEnvelope {
  success: boolean;
  code: string;
  message: string;
  data: ProveedorTarifasItem[];
}

export type GetProvidersByServiceCode =
  | 'PROVIDERS_FOUND'
  | 'NO_SERVICES_FOUND'
  | 'NO_PROVIDERS_LINKED'
  | 'VALIDATION_ERROR'
  | 'SERVICE_SEARCH_FAILED'
  | 'TARIFA_LOOKUP_FAILED'
  | 'PROVIDER_LOOKUP_FAILED'
  | 'INTERNAL_ERROR'
  | string;

export interface GetProvidersByServiceClientError {
  code: GetProvidersByServiceCode;
  message: string;
  /** VALIDATION_ERROR → marcar nombreServicio. */
  validationNombreServicio: boolean;
  retryable: boolean;
}

export function isProvidersFound(code: string | null | undefined): boolean {
  return code === 'PROVIDERS_FOUND';
}

export function isProvidersEmptySuccess(code: string | null | undefined): boolean {
  return code === 'NO_SERVICES_FOUND' || code === 'NO_PROVIDERS_LINKED';
}

export function normalizeProvidersByServiceEnvelope(
  raw: Partial<GetProvidersByServiceEnvelope> | null | undefined
): GetProvidersByServiceEnvelope {
  return {
    success: Boolean(raw?.success),
    code: String(raw?.code ?? ''),
    message: String(raw?.message ?? ''),
    data: Array.isArray(raw?.data) ? raw!.data! : []
  };
}

/**
 * Extrae code/message desde errors[] de GraphQL (Sourcing).
 * Prioriza extensions.response.{code,message}.
 */
export function parseProvidersByServiceGraphqlError(
  errors: Array<{
    message?: string;
    extensions?: {
      response?: { code?: string; message?: string };
      code?: string;
    };
  }> | null | undefined
): GetProvidersByServiceClientError {
  const first = errors?.[0];
  const response = first?.extensions?.response;
  const code = String(
    response?.code ?? first?.extensions?.code ?? 'INTERNAL_ERROR'
  );
  const message = String(
    response?.message ?? first?.message ?? 'Ocurrió un error'
  );
  const retryable =
    code === 'SERVICE_SEARCH_FAILED' ||
    code === 'TARIFA_LOOKUP_FAILED' ||
    code === 'PROVIDER_LOOKUP_FAILED' ||
    code === 'INTERNAL_ERROR';

  return {
    code,
    message,
    validationNombreServicio: code === 'VALIDATION_ERROR',
    retryable
  };
}

export function toProvidersByServiceClientError(
  err: unknown
): GetProvidersByServiceClientError {
  if (
    err &&
    typeof err === 'object' &&
    'message' in err &&
    'code' in err &&
    'validationNombreServicio' in err
  ) {
    return err as GetProvidersByServiceClientError;
  }

  const httpErrors = (err as any)?.error?.errors;
  if (Array.isArray(httpErrors)) {
    return parseProvidersByServiceGraphqlError(httpErrors);
  }

  const message =
    err instanceof Error
      ? err.message
      : typeof err === 'string'
        ? err
        : 'Ocurrió un error';

  return {
    code: 'INTERNAL_ERROR',
    message,
    validationNombreServicio: false,
    retryable: true
  };
}
